import { produce } from "immer";

const initialState = {
  ims: {
    profile: null,
    org: null,
    token: null,
    refreshToken: null,
    isLoggedIn: false,
    orgTokens: {}, // Store organization tokens by org ID
  },
  locale: "en-US",
  redirectPath: null,
};

export const createImsSlice = (set) => ({
  ...initialState,
  /**
   * Sets the IMS and locale state.
   *
   * @param {object} param0 - The new IMS and locale state.
   *
   * @returns {void}
   */
  setIms: ({ ims, locale }) =>
    set(
      (state) => ({
        ...state,
        ims,
        locale,
        isLoggedIn: !!ims.token && !!ims?.profile?.email,
      }),
      undefined,
      "ims/setIms",
    ),
  /**
   * Sets the locale.
   *
   * @param {string} locale - The new locale.
   *
   * @returns {void}
   */
  setLocale: (locale) =>
    set(
      produce((state) => {
        state.locale = locale;
      }),
      undefined,
      "ims/setLocale",
    ),
  /**
   * Sets the token.
   *
   * @param {string} token - The new token.
   *
   * @returns {void}
   */
  setToken: (token) =>
    set(
      produce((state) => {
        state.ims.token = token;
      }),
      undefined,
      "ims/setToken",
    ),
  /**
   * Sets the refresh token.
   *
   * @param {string} refreshToken - The new refresh token.
   *
   * @returns {void}
   */
  setRefreshToken: (refreshToken) =>
    set(
      produce((state) => {
        state.ims.refreshToken = refreshToken;
      }),
      undefined,
      "ims/setRefreshToken",
    ),
  /**
   * Sets the organization ID.
   *
   * @param {string} org - The new organization ID.
   *
   * @returns {void}
   */
  setImsOrg: (org) =>
    set(
      produce((state) => {
        state.ims.org = org;
      }),
      undefined,
      "ims/setImsOrg",
    ),
  /**
   * Sets the redirect path.
   *
   * @param {string} path - The new redirect path.
   *
   * @returns {void}
   */
  setRedirectPath: (path) =>
    set({ redirectPath: path }, undefined, "ims/setRedirectPath"),
  /**
   * Sets an organization token.
   *
   * @param {string} orgId - The organization ID.
   * @param {string} token - The organization token.
   *
   * @returns {void}
   */
  setOrgToken: (orgId, token) =>
    set(
      produce((state) => {
        state.ims.orgTokens = {...(state.ims.orgTokens ?? {}), [orgId]: token};
      }),
      undefined,
      "ims/setOrgToken",
    ),
  /**
   * Gets an organization token.
   *
   * @param {string} orgId - The organization ID.
   *
   * @returns {string|null} The organization token or null if not found.
   */
  getOrgToken: (orgId) => (state) => state.ims.orgTokens[orgId] || null,
  /**
   * Clears the IMS state.
   *
   * @returns {void}
   */
  clearIms: () =>
    set(
      (state) => ({
        ...state,
        ...initialState,
      }),
      undefined,
      "ims/clearIms",
    ),
});
